import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import {
  faCheck,
  faChevronLeft,
  faChevronRight,
  faEarthOceania,
} from '@fortawesome/pro-regular-svg-icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import ToolbarPopperTransition from 'elements/TipTapToolbar/ToolbarPopperTransition';
import { useLocale } from 'hooks/useLocale';
import {
  getCountriesByRegion,
  getCountryFlagEmoji,
  getLanguageOption,
  getRootLanguage,
  type CountryCode,
  type CountryOption,
  type LocaleCode,
} from 'utils/LocaleUtils';

const COUNTRY_GROUPS = getCountriesByRegion();

const CountryMenu = () => {
  const t = useTranslations();
  const { locale, region, setLocale } = useLocale();
  const [view, setView] = useState<'root' | 'detail'>('root');
  const [detailCountry, setDetailCountry] = useState<CountryOption | null>(
    null,
  );

  return (
    <Popover className="relative ml-3">
      {({ open, close }) => {
        const previousOpen = useRef(open);

        useEffect(() => {
          if (!previousOpen.current && open) {
            setView('root');
            setDetailCountry(null);
          }
          previousOpen.current = open;
        }, [open]);

        const selectLocale = (localeCode: LocaleCode) => {
          setLocale(localeCode);
          close();
        };

        const onCountryClick = (country: CountryOption) => {
          // Multi-locale countries (CA, CH) need a language choice first.
          if (country.locales.length > 1) {
            setDetailCountry(country);
            setView('detail');
            return;
          }
          selectLocale(country.locales[0]);
        };

        return (
          <>
            <PopoverButton
              className={classNames(
                styles.trigger.base,
                open && styles.trigger.active,
              )}
              aria-label={t('country.chooseCountry')}
            >
              <FontAwesomeIcon
                icon={faEarthOceania}
                className={styles.trigger.globeIcon}
                aria-hidden="true"
              />
            </PopoverButton>

            <ToolbarPopperTransition>
              <PopoverPanel
                anchor="bottom end"
                className={styles.panel.root}
                style={{ width: 300 }}
              >
                <div className={styles.panel.inner}>
                  {view === 'root' ? (
                    <>
                      <div className={styles.panel.header}>
                        <div>
                          <p className={styles.panel.headerTitle}>
                            {t('country.chooseCountry')}
                          </p>
                          <p className={styles.panel.headerSubtitle}>
                            {t('country.availableSubtitle')}
                          </p>
                        </div>
                      </div>

                      <div className={styles.panel.list}>
                        {COUNTRY_GROUPS.map(([groupRegion, countries]) => (
                          <div key={groupRegion} className={styles.group.root}>
                            <p className={styles.group.label}>{groupRegion}</p>
                            {countries.map((country) => {
                              const isSelected = country.code === region;
                              const hasVariants = country.locales.length > 1;

                              return (
                                <button
                                  key={country.code}
                                  type="button"
                                  className={
                                    isSelected
                                      ? styles.item.activeRoot
                                      : styles.item.root
                                  }
                                  onClick={() => onCountryClick(country)}
                                >
                                  <span className={styles.item.leading}>
                                    <span
                                      className={styles.item.flag}
                                      aria-hidden="true"
                                    >
                                      {getCountryFlagEmoji(country.code)}
                                    </span>
                                    <span className={styles.item.name}>
                                      {country.name}
                                    </span>
                                  </span>
                                  <span className={styles.item.trailing}>
                                    {isSelected ? (
                                      <FontAwesomeIcon
                                        icon={faCheck}
                                        className={styles.item.check}
                                        aria-hidden="true"
                                      />
                                    ) : hasVariants ? (
                                      <FontAwesomeIcon
                                        icon={faChevronRight}
                                        className={styles.item.chevron}
                                        aria-hidden="true"
                                      />
                                    ) : null}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles.detail.header}>
                        <button
                          type="button"
                          className={styles.detail.backButton}
                          onClick={() => setView('root')}
                        >
                          <FontAwesomeIcon
                            icon={faChevronLeft}
                            className={styles.detail.backIcon}
                            aria-hidden="true"
                          />
                          <span>{t('common.back')}</span>
                        </button>
                      </div>

                      <div className={styles.panel.header}>
                        <div>
                          <p className={styles.panel.headerTitle}>
                            {t('country.chooseLanguage')}
                          </p>
                          <p className={styles.panel.headerSubtitle}>
                            {t('country.languageSubtitle', {
                              country: detailCountry?.name,
                            })}
                          </p>
                        </div>
                      </div>

                      <div className={styles.panel.list}>
                        {detailCountry?.locales.map((localeCode) => {
                          const languageLabel = getLanguageOption(
                            getRootLanguage(localeCode),
                          ).label;

                          return (
                            <button
                              key={localeCode}
                              type="button"
                              className={
                                localeCode === locale
                                  ? styles.item.activeDetail
                                  : styles.item.detail
                              }
                              onClick={() => selectLocale(localeCode)}
                            >
                              <span>{languageLabel}</span>
                              {localeCode === locale ? (
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  className={styles.item.check}
                                  aria-hidden="true"
                                />
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </PopoverPanel>
            </ToolbarPopperTransition>
          </>
        );
      }}
    </Popover>
  );
};

export default CountryMenu;

// Re-exported so callers can reference the catalogue type without importing
// from two places. Keeps parity with how LocaleMenu surfaces its data.
export type { CountryCode };

const styles = {
  // Quiet round icon button, matching the visitor header's theme toggle
  // (see VisitorHeader styles.themeToggle); `active` = the popover is open
  // and holds the hover look.
  trigger: {
    base: classNames(
      'flex h-9 w-9 items-center justify-center rounded-full transition-colors focus:outline-none',
      'text-mf-gray-500 hover:bg-black/5 hover:text-mf-off-black-950',
      'marketing-dark:text-white/60 marketing-dark:hover:bg-white/10 marketing-dark:hover:text-white',
    ),
    active: classNames(
      'bg-black/5 text-mf-off-black-950',
      'marketing-dark:bg-white/10 marketing-dark:text-white',
    ),
    globeIcon: 'h-4 w-4',
  },
  panel: {
    // The anchored panel: Headless UI sets an inline max-height here (sized to
    // the viewport), so the height cap lives on `inner` instead — otherwise it
    // gets overridden and the list never scrolls.
    root: 'z-30 mt-2 overflow-hidden rounded-2xl border border-mf-gray-200 bg-white shadow-lg focus:outline-none',
    // The scroll container — the header and country list scroll together.
    // Padding lives here (not on root) so the scrollbar sits flush against the
    // panel's right edge rather than floating inside the panel padding.
    inner: 'flex max-h-80 flex-col overflow-y-auto p-2',
    header: 'mb-2 rounded-xl bg-mf-gray-50 px-3 py-3',
    headerTitle: 'text-base font-semibold text-mf-off-black-950',
    headerSubtitle: 'mt-0.5 text-[13px] leading-snug text-mf-gray-500',
    list: 'flex flex-col gap-1',
  },
  group: {
    root: 'flex flex-col',
    label: 'px-3 pb-1 pt-2 text-[13px] font-medium text-mf-gray-400',
  },
  item: {
    root: 'flex min-h-[42px] items-center justify-between gap-2 rounded-xl px-3 text-left transition-colors duration-150 hover:bg-mf-gray-100',
    activeRoot:
      'flex min-h-[42px] items-center justify-between gap-2 rounded-xl bg-mf-gray-50 px-3 text-left',
    name: 'text-sm font-medium text-mf-off-black-950',
    leading: 'inline-flex min-w-0 items-center gap-2.5',
    // Flag sits in a fixed-width, subtly framed chip so names stay aligned and
    // the emoji reads as a little flag rather than loose glyph.
    flag: 'flex h-[18px] w-[24px] flex-shrink-0 items-center justify-center overflow-hidden rounded-[4px] text-[15px] leading-none shadow-[0_0_0_1px_rgba(0,0,0,0.06)]',
    trailing: 'inline-flex items-center gap-2',
    chevron: 'h-3 w-3 text-mf-gray-400',
    check: 'h-3.5 w-3.5 text-mf-blue',
    detail:
      'flex min-h-[42px] items-center justify-between rounded-xl px-3 text-left text-sm font-medium text-mf-off-black-950 transition-colors duration-150 hover:bg-mf-gray-100',
    activeDetail:
      'flex min-h-[42px] items-center justify-between rounded-xl bg-mf-gray-100 px-3 text-left text-sm font-medium text-mf-off-black-950',
  },
  detail: {
    header: 'mb-2 flex items-center gap-2 px-1',
    backButton:
      'inline-flex items-center gap-2 rounded-xl px-2 py-2 text-sm font-medium text-mf-gray-600 transition-colors duration-150 hover:bg-mf-gray-100 hover:text-mf-gray-800',
    backIcon: 'h-3 w-3',
  },
};
